import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  Plus,
  Trash2,
  Copy,
  GripVertical,
  TrendingUp,
  CalendarClock,
  Users as UsersIcon,
  Rocket,
  Activity,
  UserX,
} from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const GRID = "28px 1.3fr 1fr 170px 150px 110px 130px 150px minmax(160px,1fr) 110px";

export const ADOPTION_STAGES = ["Not Started", "Piloting", "Active", "Scaled", "Churned"];
export const USAGE_FREQUENCIES = ["Daily", "Weekly", "Monthly", "Rarely"];

const STAGE_META = {
  "Not Started": { bg: "bg-slate-100",  text: "text-slate-600" },
  "Piloting":    { bg: "bg-butter-100", text: "text-amber-700" },
  "Active":      { bg: "bg-sky-100",    text: "text-sky-700" },
  "Scaled":      { bg: "bg-mint-100",   text: "text-emerald-700" },
  "Churned":     { bg: "bg-rose-100",   text: "text-rose-700" },
};

const isValidISODate = (v) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v || "")) return false;
  const [y, m, d] = v.split("-").map(Number);
  return m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2999;
};
const fmtShortDate = (iso) => {
  if (!isValidISODate(iso)) return "";
  const [, m, d] = iso.split("-").map(Number);
  return `${d}/${m}`;
};

function LastActivityCell({ row, onUpdate, testid }) {
  const [open, setOpen] = useState(false);
  const prop = row.last_activity || "";
  const [val, setVal] = useState(prop);
  useEffect(() => { if (open) setVal(prop); /* eslint-disable-next-line */ }, [open]);

  const commit = (raw) => {
    const v = isValidISODate(raw) ? raw : "";
    if (v === prop) return;
    onUpdate(row.id, { last_activity: v });
  };
  const label = fmtShortDate(prop) || "Set date";

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) commit(val); }}>
      <PopoverTrigger asChild>
        <button
          className={`h-8 w-full px-2 rounded-md border text-xs text-left flex items-center gap-1.5 transition-colors ${
            prop ? "border-slate-200 text-slate-700 bg-white" : "border-dashed border-slate-300 text-slate-400 hover:border-slate-400"
          }`}
          data-testid={testid}
        >
          <CalendarClock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className="truncate">{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-3 space-y-2" align="start">
        <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Last activity</label>
        <input
          type="date"
          value={val}
          min="2000-01-01"
          max="2999-12-31"
          onChange={(e) => setVal(e.target.value)}
          onBlur={() => commit(val)}
          className="w-full h-9 px-2 rounded-md border border-slate-200 text-sm"
          data-testid={`${testid}-input`}
        />
        {(val || prop) && (
          <button
            onClick={() => { setVal(""); setOpen(false); if (prop) onUpdate(row.id, { last_activity: "" }); }}
            className="text-xs text-rose-600 hover:underline"
            data-testid={`${testid}-clear`}
          >
            Clear date
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function AdoptionRow({ r, members, onUpdate, onDuplicate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: r.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, gridTemplateColumns: GRID };
  const sMeta = STAGE_META[r.stage] || STAGE_META["Not Started"];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="grid gap-2 px-4 h-12 items-center border-b border-slate-100 hover:bg-slate-50/40 group"
      data-testid={`adoption-row-${r.id}`}
    >
      <div className="flex justify-center">
        <button {...attributes} {...listeners} className="opacity-0 group-hover:opacity-100 cursor-grab text-slate-400 hover:text-slate-700" data-testid={`adoption-drag-${r.id}`}>
          <GripVertical className="h-4 w-4" />
        </button>
      </div>
      <Input
        defaultValue={r.agent}
        onBlur={(e) => { const v = e.target.value.trim(); if (v !== r.agent) onUpdate(r.id, { agent: v }); }}
        placeholder="Agent / feature"
        className="h-8 text-sm font-medium"
        data-testid={`adoption-agent-${r.id}`}
      />
      <Input
        defaultValue={r.team}
        onBlur={(e) => { const v = e.target.value.trim(); if (v !== r.team) onUpdate(r.id, { team: v }); }}
        placeholder="Team"
        className="h-8 text-sm"
        data-testid={`adoption-team-${r.id}`}
      />
      <Select value={r.owner || "__none__"} onValueChange={(v) => onUpdate(r.id, { owner: v === "__none__" ? "" : v })}>
        <SelectTrigger className="h-8 text-xs" data-testid={`adoption-owner-${r.id}`}>
          <SelectValue placeholder="Owner" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__"><span className="text-slate-500">Unassigned</span></SelectItem>
          {members.map((m) => <SelectItem key={m.id} value={m.email}>{m.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={r.stage || "Not Started"} onValueChange={(v) => onUpdate(r.id, { stage: v })}>
        <SelectTrigger className={`h-8 text-xs border-0 font-medium ${sMeta.bg} ${sMeta.text}`} data-testid={`adoption-stage-${r.id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ADOPTION_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input
        type="number"
        min="0"
        defaultValue={r.active_users ?? 0}
        onBlur={(e) => {
          const v = e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0);
          if (v !== (r.active_users ?? 0)) onUpdate(r.id, { active_users: v });
        }}
        placeholder="0"
        className="h-8 text-sm text-center"
        data-testid={`adoption-users-${r.id}`}
      />
      <Select value={r.usage_frequency || "Weekly"} onValueChange={(v) => onUpdate(r.id, { usage_frequency: v })}>
        <SelectTrigger className="h-8 text-xs" data-testid={`adoption-frequency-${r.id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {USAGE_FREQUENCIES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
        </SelectContent>
      </Select>
      <div>
        <LastActivityCell row={r} onUpdate={onUpdate} testid={`adoption-activity-${r.id}`} />
      </div>
      <Input
        defaultValue={r.remarks}
        key={r.remarks}
        onBlur={(e) => { const v = e.target.value.trim(); if (v !== r.remarks) onUpdate(r.id, { remarks: v }); }}
        placeholder="Add remark…"
        className="h-8 text-sm"
        data-testid={`adoption-remark-${r.id}`}
      />
      <div className="flex items-center justify-end gap-1">
        <button onClick={() => onDuplicate(r)} className="h-8 w-8 rounded-md hover:bg-slate-100 text-slate-500 hover:text-slate-800 flex items-center justify-center" title="Duplicate" data-testid={`adoption-duplicate-${r.id}`}>
          <Copy className="h-4 w-4" />
        </button>
        <button onClick={() => onDelete(r)} className="h-8 w-8 rounded-md hover:bg-rose-50 text-slate-400 hover:text-rose-600 flex items-center justify-center" title="Delete" data-testid={`adoption-delete-${r.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3" data-testid={testid}>
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-display font-semibold text-slate-900 leading-tight">{value}</p>
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-400">{label}</p>
      </div>
    </div>
  );
}

export default function Adoption() {
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const load = async () => {
    try {
      const { data } = await api.get("/adoption-records");
      setRows(data);
    } catch { toast.error("Failed to load adoption"); }
    finally { setLoading(false); }
  };
  const loadMembers = async () => {
    try { const { data } = await api.get("/members"); setMembers(data); } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadMembers(); }, []);

  const stats = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => r.stage === "Active" || r.stage === "Scaled").length;
    const piloting = rows.filter((r) => r.stage === "Piloting").length;
    const churned = rows.filter((r) => r.stage === "Churned").length;
    const activeUsers = rows.reduce((acc, r) => acc + (parseInt(r.active_users, 10) || 0), 0);
    return { total, active, piloting, churned, activeUsers };
  }, [rows]);

  const handleCreate = async () => {
    try {
      const { data } = await api.post("/adoption-records", {});
      setRows((prev) => [...prev, data]);
    } catch { toast.error("Failed to add row"); }
  };
  const handleUpdate = async (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    try {
      const { data } = await api.patch(`/adoption-records/${id}`, patch);
      setRows((prev) => prev.map((r) => (r.id === id ? data : r)));
      return data;
    } catch { toast.error("Update failed"); load(); }
  };
  const handleDelete = async (r) => {
    if (!window.confirm(`Delete "${r.agent || "this row"}"?`)) return;
    try {
      await api.delete(`/adoption-records/${r.id}`);
      setRows((prev) => prev.filter((x) => x.id !== r.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };
  const handleDuplicate = async (r) => {
    try { await api.post(`/adoption-records/${r.id}/duplicate`); await load(); toast.success("Duplicated"); }
    catch { toast.error("Duplicate failed"); }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = rows.findIndex((r) => r.id === active.id);
    const newIndex = rows.findIndex((r) => r.id === over.id);
    const next = arrayMove(rows, oldIndex, newIndex);
    setRows(next);
    try { await api.post("/adoption-records/reorder", { order: next.map((r) => r.id) }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  return (
    <div className="space-y-6" data-testid="adoption-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-amber-600" /> Adoption
          </h2>
          <p className="text-sm text-slate-500">Track how teams adopt each agent — stage, active users, frequency and last activity.</p>
        </div>
        <Button onClick={handleCreate} className="btn-brand h-10 rounded-xl" data-testid="adoption-new-row">
          <Plus className="h-4 w-4 mr-1" /> Add record
        </Button>
      </div>

      {/* KPI summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiCard icon={UsersIcon} label="Adopters"     value={stats.total}       tone="bg-slate-100 text-slate-600"    testid="adoption-kpi-total" />
        <KpiCard icon={Activity}  label="Active"        value={stats.active}      tone="bg-sky-50 text-sky-600"         testid="adoption-kpi-active" />
        <KpiCard icon={Rocket}    label="Piloting"      value={stats.piloting}    tone="bg-butter-100 text-amber-600"   testid="adoption-kpi-piloting" />
        <KpiCard icon={UserX}     label="Churned"       value={stats.churned}     tone="bg-rose-50 text-rose-600"       testid="adoption-kpi-churned" />
        <KpiCard icon={TrendingUp} label="Active users" value={stats.activeUsers} tone="bg-mint-100 text-emerald-600"   testid="adoption-kpi-users" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <div style={{ minWidth: 1250 }}>
          <div
            className="grid gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 px-4 h-11 items-center bg-slate-100/70 border-b border-slate-200 whitespace-nowrap"
            style={{ gridTemplateColumns: GRID }}
          >
            <div></div>
            <div>Agent</div>
            <div>Team</div>
            <div>Owner</div>
            <div>Stage</div>
            <div className="text-center">Users</div>
            <div>Frequency</div>
            <div>Last Activity</div>
            <div>Remarks</div>
            <div className="text-right pr-2">Actions</div>
          </div>

          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400" data-testid="adoption-empty">
              No adoption records yet — click "Add record"
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={rows.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                {rows.map((r) => (
                  <AdoptionRow
                    key={r.id}
                    r={r}
                    members={members}
                    onUpdate={handleUpdate}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>
    </div>
  );
}
